/** Where each Lamp Run cell sits on the ring. */
export interface RingPoint {
  x: number;
  y: number;
  /** Radians from the centre; the first cell is at the top (-PI/2). */
  angle: number;
}

/**
 * Lay `count` cells evenly round an ellipse centred on (cx, cy), starting at
 * the top and going clockwise, the way the ring art is painted.
 */
export function ringLayout(count: number, cx: number, cy: number, rx: number, ry: number = rx): RingPoint[] {
  const points: RingPoint[] = [];
  for (let i = 0; i < count; i++) {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / count;
    points.push({ x: cx + rx * Math.cos(angle), y: cy + ry * Math.sin(angle), angle });
  }
  return points;
}

/**
 * Where the hub's RUN POT label and the pot under it go, so the two read as
 * one pair centred on the plate at `cy` however many lines the pot has
 * (coins, gems and free spins each take one). Both are placed by their top
 * edge, `gap` apart; returns each top, in whole pixels.
 */
export function hubPair(cy: number, labelHeight: number, valueHeight: number, gap = 2): { labelY: number; valueY: number } {
  const labelY = Math.round(cy - (labelHeight + gap + valueHeight) / 2);
  return { labelY, valueY: labelY + Math.round(labelHeight + gap) };
}

/**
 * The chase path from `from` to `target`: at least `minLaps` full laps, then
 * on round to the target, so a quick server answer still looks like a spin.
 * Returns the cell indices in order, ending on `target`.
 */
export function chasePath(count: number, from: number, target: number, minLaps = 1): number[] {
  const path: number[] = [];
  const steps = minLaps * count + ((target - from + count) % count);
  for (let s = 1; s <= steps; s++) path.push((from + s) % count);
  return path;
}

/**
 * How long each chase step waits, easing out over the last part of the path
 * so the light slows onto its cell. `base` is the fastest step in ms.
 */
export function chaseDelays(steps: number, base = 28, ease = 220): number[] {
  const delays: number[] = [];
  for (let s = 0; s < steps; s++) {
    const t = steps <= 1 ? 1 : s / (steps - 1);
    delays.push(Math.round(base + ease * Math.pow(t, 2.6)));
  }
  return delays;
}
